import { info } from '@/config'
import { isModuleEnabled } from '@/config/@system/modules'

/**
 * One entry of the route table (AppRoutes.jsx `systemRoutes`, routes/@custom `customRoutes`).
 * @typedef {object} RouteDef
 * @property {string} path
 * @property {import('react').ReactNode} element
 * @property {string} [module]       feature module that must be enabled (config/@system/modules.js)
 * @property {RouteDef[]} [children]  nested routes, merged recursively by mergeRoutes()
 */

/**
 * Drop route definitions whose optional `module` field names a feature module
 * that brand.json switches off (e.g. `{ path: '/pricing', module: 'billing' }`).
 * Routes without a `module` field are always kept. Applied to the @system route
 * table before mergeRoutes(), so @custom routes can still re-add a path.
 * @param {RouteDef[]} routes
 * @param {(key: string) => boolean} [isEnabled]
 * @returns {RouteDef[]}
 */
export function filterRoutesByModules(routes, isEnabled = isModuleEnabled) {
  return routes.filter((r) => !r.module || isEnabled(r.module))
}

/**
 * @param {RouteDef[]} systemRoutes
 * @param {RouteDef[] | undefined} customRoutes
 * @returns {RouteDef[]}
 */
export function mergeRoutes(systemRoutes, customRoutes) {
  if (!customRoutes || customRoutes.length === 0) return systemRoutes
  /** @type {Map<string, RouteDef>} */
  const merged = new Map()
  for (const route of systemRoutes) merged.set(route.path, route)
  for (const route of customRoutes) {
    const existing = merged.get(route.path)
    if (existing && route.children && existing.children) {
      merged.set(route.path, { ...existing, ...route, children: mergeRoutes(existing.children, route.children) })
    } else {
      merged.set(route.path, route)
    }
  }
  return Array.from(merged.values())
}

/** @typedef {{ plan?: string, name?: string, priceId?: string, noAllowedRoutes?: string[] }} RoutePlan */

/**
 * Plans in tier order. brand.json `pricing.plans` (surfaced as info.pricing by the generated
 * config/@custom/info.js) is the single source shared with the /pricing page and the server plan
 * gate (server/src/lib/@system/Billing/plans.js); the legacy `info.plans` array is the fallback.
 * @param {{ pricing?: { plans?: unknown } | null, plans?: unknown } | null} [cfg]
 * @returns {RoutePlan[]}
 */
export function getPlans(cfg = info) {
  const fromBrand = cfg?.pricing?.plans
  if (Array.isArray(fromBrand) && fromBrand.length) return fromBrand
  return Array.isArray(cfg?.plans) ? cfg.plans : []
}

/** @param {unknown} value */
const slugOf = (value) => String(value ?? '').toLowerCase().trim()

/**
 * Is `pathname` locked for `userPlan` (slug, display name or price id; absent = plans[0])?
 * Patterns are exact or `prefix/*` — the same rule as the server's planApiGate.
 * @param {string | null | undefined} pathname
 * @param {string | null} [userPlan]   plan slug, display name or Stripe priceId
 * @param {RoutePlan[] | null} [plans]
 */
export function isRouteLocked(pathname, userPlan, plans = getPlans()) {
  if (!pathname) return false
  if (!plans || !Array.isArray(plans) || plans.length === 0) return false
  const needle = slugOf(userPlan)
  const plan = needle
    ? (plans.find(p => slugOf(p.plan) === needle || slugOf(p.name) === needle || p.priceId === userPlan) ?? plans[0])
    : plans[0]
  if (!plan || !plan.noAllowedRoutes || plan.noAllowedRoutes.length === 0) return false
  return plan.noAllowedRoutes.some(locked => {
    if (locked === pathname) return true
    if (locked.endsWith('/*')) {
      const prefix = locked.slice(0, -2)
      return pathname === prefix || pathname.startsWith(prefix + '/')
    }
    return false
  })
}
